import type { MqttClient, IClientOptions } from 'mqtt';

export type AclStatus = 'disconnected' | 'connected' | 'reconnecting' | 'error';
export interface MqttIdentity {
  principalId: string;
  tenantId: string;
  // Change on token, role, department or grant changes; remove adapter on logout.
  revision: string;
}
export interface UserMqttCredential {
  kind: 'user';
  username: string;
  password: string;
  expires_at: string;
  expires_in: number;
  subscribe: string[];
  publish: string[];
}
export interface MqttAclAdapter {
  identity: MqttIdentity;
  brokerUrl: string;
  // Integration must use the verified user JWT/tenant. Never use guest/site credentials.
  mint: (identity: Readonly<MqttIdentity>, signal: AbortSignal) => Promise<UserMqttCredential>;
}
type Connect = (url: string, options: IClientOptions) => MqttClient;
type MessageHandler = (message: string) => void;
const DEMAND = 'eternal/demand';
const validId = (value: unknown): value is string => typeof value === 'string' &&
  value.length > 0 && !/[\u0000/+#]/.test(value);
const validFilter = (value: unknown): value is string => typeof value === 'string' &&
  value.length > 0 && !value.includes('\u0000') && value.split('/').every((part, index, parts) =>
    (!part.includes('+') || part === '+') && (!part.includes('#') || (part === '#' && index === parts.length - 1)));
const coversDemand = (filter: string) => {
  const parts = filter.split('/');
  const levels = DEMAND.split('/');
  return parts.every((part, index) => part === '#' || (index < levels.length && (part === '+' || part === levels[index]))) &&
    (parts[parts.length - 1] === '#' || parts.length === levels.length);
};

// Only the verified demand contract is supported. No livestream/notification grant is invented.
export class AclSession {
  private client: MqttClient | null = null;
  private controller: AbortController | null = null;
  private timer: ReturnType<typeof setTimeout> | undefined;
  private expiry = 0;
  private generation = 0;
  private stopped = false;
  private canPublish = false;
  private subscribed = false;
  private subscriptionRevision = 0;
  private subscribing = false;
  private desired = new Map<MessageHandler, boolean>();
  private identity: Readonly<MqttIdentity>;

  constructor(
    private adapter: MqttAclAdapter,
    private connect: Connect,
    private status: (status: AclStatus) => void
  ) {
    this.identity = Object.freeze({ ...adapter.identity });
  }

  start() { void this.refresh(); }

  private close() {
    this.generation += 1;
    clearTimeout(this.timer);
    this.controller?.abort();
    this.controller = null;
    this.canPublish = false;
    this.subscribed = false;
    this.subscribing = false;
    this.subscriptionRevision += 1;
    this.expiry = 0;
    const client = this.client;
    this.client = null;
    if (client) {
      client.removeAllListeners();
      client.options.username = undefined;
      client.options.password = undefined;
      client.end(true);
    }
  }

  stop() {
    this.stopped = true;
    this.close();
    this.desired.clear();
  }

  private retry() {
    if (this.stopped) return;
    this.close();
    this.status('error');
    this.timer = setTimeout(() => { void this.refresh(); }, 5000);
  }

  private async refresh() {
    if (this.stopped) return;
    this.close();
    const generation = this.generation;
    this.status('reconnecting');
    const controller = new AbortController();
    this.controller = controller;
    try {
      const url = new URL(this.adapter.brokerUrl);
      if (url.protocol !== 'wss:' || url.username || url.password || url.search || url.hash ||
          !validId(this.identity.principalId) || !validId(this.identity.tenantId) ||
          typeof this.identity.revision !== 'string' || !this.identity.revision) {
        throw new Error('Invalid ACL configuration');
      }
      let timeout: ReturnType<typeof setTimeout> | undefined;
      let cancel: () => void = () => {};
      let credential: UserMqttCredential;
      try {
        const cancelled = new Promise<never>((_, reject) => {
          cancel = () => reject(new Error('MQTT credential request cancelled'));
          controller.signal.addEventListener('abort', cancel, { once: true });
          timeout = setTimeout(() => controller.abort(), 10000);
        });
        credential = await Promise.race([this.adapter.mint(this.identity, controller.signal), cancelled]);
      } finally {
        clearTimeout(timeout);
        controller.signal.removeEventListener('abort', cancel);
      }
      if (this.stopped || generation !== this.generation) return;
      const expiry = Date.parse(credential.expires_at);
      const ttl = Math.min(expiry - Date.now(), credential.expires_in * 1000);
      if (!credential || credential.kind !== 'user' || credential.username !== this.identity.principalId ||
          typeof credential.password !== 'string' || !credential.password ||
          typeof credential.expires_at !== 'string' || typeof credential.expires_in !== 'number' ||
          !Number.isFinite(credential.expires_in) || !Number.isFinite(ttl) || ttl <= 1000 ||
          !Array.isArray(credential.subscribe) || !credential.subscribe.every(validFilter) ||
          !credential.subscribe.some(coversDemand) || !Array.isArray(credential.publish) ||
          !credential.publish.every(topic => typeof topic === 'string' && topic.length > 0 && !/[\u0000+#]/.test(topic))) {
        throw new Error('Invalid ACL credential');
      }
      this.expiry = Date.now() + ttl;
      this.canPublish = credential.publish.includes(DEMAND);
      const client = this.connect(url.toString(), {
        username: credential.username,
        password: credential.password,
        clientId: `peace-${Math.random().toString(16).slice(2)}-${Date.now()}`,
        clean: true,
        reconnectPeriod: 0,
        resubscribe: false,
        keepalive: 60,
        connectTimeout: 10000
      });
      this.client = client;
      const current = () => !this.stopped && generation === this.generation && this.client === client;
      this.timer = setTimeout(() => { void this.refresh(); }, Math.min(ttl * 0.8, 2147483647));
      client.on('connect', () => {
        if (!current()) return;
        this.status('connected');
        this.restoreSubscriptions();
      });
      client.on('message', (topic, payload) => {
        if (!current() || Date.now() >= this.expiry || !this.subscribed || topic !== DEMAND) return;
        this.desired.forEach((_, callback) => callback(payload.toString()));
      });
      (['error', 'close', 'offline'] as const).forEach(event => {
        client.on(event, () => { if (current()) this.retry(); });
      });
    } catch {
      // Credential/transport errors may contain secrets: never log the error object.
      if (!this.stopped && generation === this.generation) this.retry();
    }
  }

  private restoreSubscriptions() {
    const client = this.client;
    if (!client?.connected || !this.desired.size || this.subscribed || this.subscribing) return;
    const generation = this.generation;
    const revision = this.subscriptionRevision;
    this.subscribing = true;
    try {
      client.subscribe(DEMAND, (error, grants) => {
        if (this.stopped || generation !== this.generation || this.client !== client ||
            revision !== this.subscriptionRevision || !this.desired.size) return;
        this.subscribing = false;
        if (error || grants?.length !== 1 || grants[0].topic !== DEMAND || ![0, 1, 2].includes(grants[0].qos)) {
          this.retry();
          return;
        }
        this.subscribed = true;
      });
    } catch { this.retry(); }
  }

  subscribe(topic: string, callback: MessageHandler): boolean {
    if (this.stopped || topic !== DEMAND) return false;
    this.desired.set(callback, true);
    this.restoreSubscriptions();
    return true;
  }

  unsubscribe(topic: string): boolean {
    if (topic !== DEMAND) return false;
    this.desired.clear();
    this.subscribed = false;
    this.subscribing = false;
    this.subscriptionRevision += 1;
    if (this.client?.connected) {
      try { this.client.unsubscribe(DEMAND); }
      catch { this.retry(); return false; }
    }
    return true;
  }

  publish(topic: string, payload: string): boolean {
    if (this.stopped || topic !== DEMAND || !this.canPublish ||
        !this.client?.connected || Date.now() >= this.expiry) return false;
    const client = this.client;
    const generation = this.generation;
    try {
      client.publish(topic, payload, { qos: 0, retain: false }, error => {
        if (error && !this.stopped && generation === this.generation && this.client === client) this.retry();
      });
      return true;
    } catch {
      this.retry();
      return false;
    }
  }
}
